<template>
  <article class="panel panel-section request-staff-hour-panel p-bottom-4">
    <p
      class="panel-heading request-staff-hour-panel-header has-background-light has-text-weight-semibold is-size-4"
    >
      Staff time
    </p>
    <div
      class="request-staff-hour-panel-content p-bottom-8"
      v-if="!filteredRequestLoading"
    >
      <div class="p-bottom-8">
        <div
          v-for="(hours, name) in requestStaffHours"
          :name="name"
          :hours="hours"
          :key="name"
        >
          <div class="has-text-weight-semibold p-left-12 p-top-4">
            {{ name }}
          </div>
          <div v-for="hour in hours" :key="hour.id">
            <NrTooltip
              v-if="hour.invoice_id != null"
              :label="
                hour.paid ? 'Paid in previous invoice' : 'Waiting for payment'
              "
              class="panel-item is-flex is-align-items-center is-justify-content-space-between request-staff-hour--sent"
            >
              {{ hour.staff_hour }}
              {{ hour.exclude_time_on_invoice ? '(Tracking Only)' : '' }}
            </NrTooltip>
            <div
              v-else
              class="panel-item is-flex is-align-items-center is-justify-content-space-between"
              :class="{
                'panel-item--editable': $can('publish', 'Request')
              }"
            >
              {{ hour.staff_hour }}
              {{ hour.exclude_time_on_invoice ? '(Tracking Only)' : '' }}
              <div>
                <NrTooltip
                  v-if="$can('edit', 'RequestHour')"
                  label="Edit staff hours"
                >
                  <NrIconButton
                    variant="minimal-neutral"
                    :icon="{ name: 'pencil-alt', type: 'regular' }"
                    aria-label="Edit staff hours"
                    @click="processEditClick(hour)"
                  />
                </NrTooltip>
                <NrTooltip
                  v-if="$can('edit', 'RequestHour')"
                  label="Delete staff hours"
                >
                  <NrIconButton
                    variant="minimal-neutral"
                    aria-label="Delete staff hours"
                    :icon="{ name: 'trash-alt', type: 'regular' }"
                    @click="
                      processDeleteClick(
                        hour,
                        'deleteStaffHourConfirmationDialog'
                      )
                    "
                  />
                </NrTooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="p-bottom-8" v-show="totalStaffHours">
        <div class="has-text-weight-semibold p-left-12">Total staff hours</div>
        <div class="p-left-24 p-top-8">{{ totalStaffHours }}</div>
      </div>
      <div class="p-bottom-8" v-show="staffCost">
        <div class="has-text-weight-semibold p-left-12">Staff cost</div>
        <div class="p-left-24 p-top-8">{{ formatCost(staffCost) }}</div>
      </div>
      <NrModal
        class="delete-staff-time"
        title="Delete Staff Time"
        confirm-button-styles="is-danger"
        primaryButtonText="Delete"
        cancel-button-styles="is-primary is-outlined"
        secondaryButtonText="Cancel"
        :hasSecondaryButton="true"
        :show="activeModal === 'deleteStaffHourConfirmationDialog'"
        :disable-submit="false"
        @on-primary-click="processDeleteStaffSubmit()"
        @on-secondary-click="setActiveModal(null)"
      >
        <p class="content">
          {{ deleteStaffHourConfirmationText }}
        </p>
      </NrModal>
    </div>
    <NrLoadingAnimation v-else :count="3" />
  </article>
</template>

<script lang="ts">
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation'
import { mapMutations, mapActions, mapState } from 'vuex'
import helpers from '../../../utils/helpers.js'

interface StaffHours {
  // eslint-disable-next-line camelcase
  department_id: number | null
  // eslint-disable-next-line camelcase
  description: string
  hours: number
  minutes: number
  // eslint-disable-next-line camelcase
  staff_hour: string
  // eslint-disable-next-line camelcase
  staff_id: number
  // eslint-disable-next-line camelcase
  staff_name: string
  id: number
}

export default {
  components: {
    NrIconButton,
    NrModal,
    NrTooltip,
    NrLoadingAnimation
  },
  props: {
    staffCost: {
      required: false,
      type: String,
      default() {
        return ''
      }
    },
    totalStaffHours: {
      required: false,
      type: String,
      default() {
        return ''
      }
    }
  },

  computed: {
    ...mapState(['currentUser']),
    ...mapState('request_show', [
      'request',
      'requestStaffHours',
      'filteredRequestLoading',
      'selectedStaffHour',
      'activeModal'
    ]),
    deleteStaffHourConfirmationText(): string {
      return `You are about to remove staff time logged for this user.
      This action cannot be undone. Do you want to continue?`
    }
  },

  methods: {
    ...mapActions('request_show', [
      'fetchRequestStaffHours',
      'processStaffHour'
    ]),

    ...mapMutations('request_show', ['setSelectedStaffHour', 'setActiveModal']),

    clearSelection(): void {
      this.setActiveModal(null)
      this.selectedInvoice = []
    },

    processDeleteClick(staffHour: StaffHours, modalName: string) {
      this.setSelectedStaffHour(staffHour)
      this.setActiveModal(modalName)
    },

    processEditClick(hour: StaffHours) {
      this.$emit('set-staff-time-for-edit', hour)
      this.setActiveModal('editStaffTime')
    },

    processDeleteStaffSubmit(): void {
      const payload = {
        id: this.selectedStaffHour.id,
        request_id: this.request.pretty_id,
        action: 'delete',
        method: 'delete'
      }
      this.processStaffHour(payload).then(() => {
        this.clearSelection()
      })
      this.setSelectedStaffHour({})
      this.setActiveModal(null)
    },

    formatCost(cost: number, maximumFractionDigits: number = 2): string {
      return helpers.formatCost(cost, maximumFractionDigits)
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';
.request-staff-hour-panel {
  .request-staff-hour-panel-content {
    padding-top: 0.75rem;
    .panel-item {
      padding-left: 1.5rem;
      margin: 0;
    }
  }
  .is-align-items-center {
    align-items: center;
  }
  .is-justify-content-space-between {
    justify-content: space-between;
  }
}
.delete-staff-time {
  .nr-modal__header {
    padding: 1.25em;
    background-color: #e02b00;
  }
  .nr-modal__title {
    font-weight: bold;
    color: #fff;
  }
  .modal__btn-primary {
    background-color: #e02b00;
  }
  .nr-modal__overlay {
    z-index: 200;
  }
}
.request-staff-hour--sent {
  color: $color-gray-80;
}
</style>
